import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { CalendarCheck, Flower2, IndianRupee, Package, Sparkles } from "lucide-react";
import HandoverAlerts from "@/components/admin/HandoverAlerts";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const Tile = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="border-border">
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <span className="overline">{label}</span>
        <Icon size={16} className="text-secondary" />
      </div>
      <div className="font-display text-3xl mt-2" data-testid={testid}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

/** Admin → Overview: how the practice is doing, all time and this month. */
export const OverviewTab = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Loading overview…</p>;

  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div>
        <h3 className="font-display text-2xl mb-1">This month · {data.month}</h3>
        <p className="text-sm text-muted-foreground">Orders, consultations, yoga and Garbhsanskar signups at a glance.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Tile icon={IndianRupee} label="Revenue this month" value={money(data.orders.revenue_this_month)}
              sub={`${money(data.orders.revenue_total)} all time`} testid="ov-revenue-month" />
        <Tile icon={Package} label="Product orders" value={data.orders.this_month}
              sub={`${data.orders.total} all time · ${data.orders.paid} paid`} testid="ov-orders-month" />
        <Tile icon={CalendarCheck} label="Appointments" value={data.appointments.this_month}
              sub={`${data.appointments.total} all time · ${data.appointments.pending} pending`} testid="ov-appts-month" />
        <Tile icon={Sparkles} label="Yoga signups" value={data.yoga.this_month}
              sub={`${data.yoga.signups_total} all time · ${data.yoga.active} active`} testid="ov-yoga-month" />
        <Tile icon={Flower2} label="Garbhsanskar signups" value={data.garbha?.this_month ?? 0}
              sub={`${data.garbha?.signups_total ?? 0} all time · ${data.garbha?.active ?? 0} active`} testid="ov-garbha-month" />
      </div>

      <Card className="border-border">
        <CardContent className="p-6">
          <h4 className="font-display text-2xl mb-4">Orders by status</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ov-by-status">
            {Object.entries(data.orders.by_status).map(([status, count]) => (
              <div key={status} className="rounded-xl border border-border p-3">
                <div className="font-display text-2xl">{count}</div>
                <div className="text-[11px] text-muted-foreground capitalize">{status.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile icon={Sparkles} label="1-on-1 booked" value={data.yoga.one_on_one_booked} testid="ov-one-on-one" />
        <Tile icon={CalendarCheck} label="Live classes" value={data.yoga.live_classes} testid="ov-live-classes" />
        <Tile icon={Package} label="Products listed" value={data.catalogue.products} testid="ov-products" />
        <Tile icon={Sparkles} label="Gurus · reviews" value={`${data.catalogue.gurus} · ${data.catalogue.reviews}`} testid="ov-gurus" />
        <Tile icon={Flower2} label="Samvad sessions" value={data.garbha?.samvad ?? 0}
              sub={`${data.garbha?.paying ?? 0} paying subscriptions`} testid="ov-garbha-samvad" />
      </div>

      <HandoverAlerts />

      {data.assistant && (
        <Card className="border-border">
          <CardContent className="p-6">
            <h4 className="font-display text-2xl mb-1">Vaidya AI handovers</h4>
            <p className="text-sm text-muted-foreground mb-5">
              Visitors the assistant sent to WhatsApp or the booking page because they needed a real vaidya.
            </p>
            <div className="flex flex-wrap items-end gap-8">
              <div>
                <div className="font-display text-3xl" data-testid="ov-handovers-month">{data.assistant.handovers_this_month}</div>
                <div className="text-[11px] text-muted-foreground">this month</div>
              </div>
              <div>
                <div className="font-display text-3xl" data-testid="ov-handovers-total">{data.assistant.handovers_total}</div>
                <div className="text-[11px] text-muted-foreground">all time</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground" data-testid="ov-handover-channel">
                  Alerts: {data.assistant.alerts?.channel === "whatsapp" ? "WhatsApp" : data.assistant.alerts?.channel === "sms" ? "SMS" : "in-panel only"}
                  {data.assistant.alerts?.destination ? ` → ${data.assistant.alerts.destination}` : ""}
                </div>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="ov-handover-reasons">
                {data.assistant.top_reasons.map((r) => (
                  <span key={r.reason} className="text-xs rounded-full border border-border px-3 py-1.5 capitalize">
                    {r.reason === "model" ? "needs a vaidya" : r.reason} · {r.count}
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OverviewTab;
